import {
	GEO_FAILURE_REASON,
	type GeoFailureReason,
} from './geo-failure-reason'

/** Message and status patterns used to classify external errors. */
const SDK_LOAD_FAILED_MSG = 'GeoLocation SDK failed to load'
const SDK_FAILED_LOAD_MSG = 'SDK failed to load'
const SDK_LOAD_TIMEOUT_MSG = 'GeoLocation SDK load timeout'
const LOCATE_DEVICE_TIMEOUT_MSG = 'locateDevice timeout'
const TOKEN_INVALID_MSG = 'GeoComply token missing or invalid'
const GEOCOMPLY_TOKEN_MSG = 'GeoComply token'
const TIMEOUT_MSG = 'timeout'
const HTTP_STATUS_UNAUTHORIZED = 401

export type GeoLocationTimeoutPhase = 'sdk_load' | 'locate_device'

export const GEO_TIMEOUT_PHASE = {
	SDK_LOAD: 'sdk_load',
	LOCATE_DEVICE: 'locate_device',
} as const satisfies Record<string, GeoLocationTimeoutPhase>

export class GeoLocationSdkLoadError extends Error {
	constructor(message = 'GeoLocation SDK failed to load') {
		super(message)
		this.name = 'GeoLocationSdkLoadError'
		Object.setPrototypeOf(this, GeoLocationSdkLoadError.prototype)
	}
}

export class GeoLocationTimeoutError extends Error {
	constructor(
		message = 'timeout',
		public readonly phase?: GeoLocationTimeoutPhase
	) {
		super(message)
		this.name = 'GeoLocationTimeoutError'
		Object.setPrototypeOf(this, GeoLocationTimeoutError.prototype)
	}
}

export class GeoLocationTokenError extends Error {
	constructor(message = 'GeoComply token error') {
		super(message)
		this.name = 'GeoLocationTokenError'
		Object.setPrototypeOf(this, GeoLocationTokenError.prototype)
	}
}

export class GeoLocationPermissionDeniedError extends Error {
	constructor(message = 'Permission denied') {
		super(message)
		this.name = 'GeoLocationPermissionDeniedError'
		Object.setPrototypeOf(this, GeoLocationPermissionDeniedError.prototype)
	}
}

export class GeoLocationUnknownError extends Error {
	constructor(message = 'Unknown geolocation error') {
		super(message)
		this.name = 'GeoLocationUnknownError'
		Object.setPrototypeOf(this, GeoLocationUnknownError.prototype)
	}
}

export type GeoLocationError =
	| GeoLocationSdkLoadError
	| GeoLocationTimeoutError
	| GeoLocationTokenError
	| GeoLocationPermissionDeniedError
	| GeoLocationUnknownError

function inferTimeoutPhaseFromMessage(msg: string): GeoLocationTimeoutPhase | undefined {
	const lower = msg.trim().toLowerCase()
	if (lower.includes(SDK_LOAD_TIMEOUT_MSG.toLowerCase())) return GEO_TIMEOUT_PHASE.SDK_LOAD
	if (lower.includes(LOCATE_DEVICE_TIMEOUT_MSG.toLowerCase())) return GEO_TIMEOUT_PHASE.LOCATE_DEVICE
	return undefined
}

export function coerceToGeoLocationError(err: unknown): GeoLocationError {
	if (err instanceof GeoLocationSdkLoadError) return err
	if (err instanceof GeoLocationTimeoutError) return err
	if (err instanceof GeoLocationTokenError) return err
	if (err instanceof GeoLocationPermissionDeniedError) return err
	if (err instanceof GeoLocationUnknownError) return err

	const errObj = err as { status?: number; message?: string }

	const msg = err instanceof Error ? err.message : String(err ?? '')
	const msgNormalized = msg.trim().toLowerCase()

	if (
		msgNormalized.includes(SDK_LOAD_FAILED_MSG.toLowerCase()) ||
		msgNormalized.includes(SDK_FAILED_LOAD_MSG.toLowerCase())
	)
		return new GeoLocationSdkLoadError(msg)

	if (msgNormalized.includes(TIMEOUT_MSG)) {
		const phase = inferTimeoutPhaseFromMessage(msg)
		return new GeoLocationTimeoutError(msg, phase)
	}

	if (
		msgNormalized.includes(TOKEN_INVALID_MSG.toLowerCase()) ||
		msgNormalized.includes(GEOCOMPLY_TOKEN_MSG.toLowerCase())
	)
		return new GeoLocationTokenError(msg)

	const status = errObj?.status
	if (status === HTTP_STATUS_UNAUTHORIZED) return new GeoLocationTokenError(msg)

	return new GeoLocationUnknownError(msg || 'Unknown geolocation error')
}

export function reasonFromGeoLocationError(err: GeoLocationError): GeoFailureReason | undefined {
	if (err instanceof GeoLocationSdkLoadError) return GEO_FAILURE_REASON.SDK_LOAD_FAILED
	if (err instanceof GeoLocationTimeoutError) return GEO_FAILURE_REASON.TIMEOUT
	if (err instanceof GeoLocationTokenError) return GEO_FAILURE_REASON.TOKEN_FAILED
	if (err instanceof GeoLocationPermissionDeniedError)
		return GEO_FAILURE_REASON.PERMISSION_DENIED
	return undefined
}
